<template>
  <div class="space-y-6">
    <!-- Filters Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <!-- Provider Filter -->
      <div class="flex items-center space-x-3">
        <label for="provider-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Provider:
        </label>
        <Select
          id="provider-filter"
          v-model="providerFilter"
          :options="providerOptions"
          :placeholder="''"
          class="w-48"
        />
      </div>

      <!-- Show Enabled Only Toggle -->
      <div class="flex items-center space-x-2">
        <input
          id="show-enabled-only"
          v-model="showEnabledOnly"
          type="checkbox"
          class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-blue-600"
        />
        <label for="show-enabled-only" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Show enabled only
        </label>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <!-- Error State -->
    <Alert v-else-if="error" variant="error" class="mb-4">
      {{ error }}
    </Alert>

    <!-- Empty State -->
    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="filteredModels.length === 0"
      class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No models found</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {{
          showEnabledOnly
            ? 'No enabled models match your filters.'
            : 'No models match your filters.'
        }}
      </p>
    </div>

    <!-- Models Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <BaseModelCard
        v-for="model in filteredModels"
        :key="model.slug"
        :model="model"
        @add="handleAdd"
        @enable="handleEnable"
        @disable="handleDisable"
        @remove="handleRemove"
      />
    </div>

    <!-- Add Base Model Modal -->
    <AddBaseModelModal
      v-if="selectedModel"
      :open="isAddModalOpen"
      :model-slug="selectedModel.slug"
      :model-name="selectedModel.name"
      :provider="selectedModel.provider"
      @update:open="isAddModalOpen = $event"
      @submit="handleAddSubmit"
      @cancel="handleAddCancel"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { Select, LoadingSpinner, Alert } from '@asabdev/asab-ui';
  import { useNotification } from '@asabdev/asab-ui';
  import BaseModelCard from './BaseModelCard.vue';
  import AddBaseModelModal from './AddBaseModelModal.vue';
  import { useBaseModels } from '../composables/useBaseModels';
  import type { BaseModelResponse } from '../types';

  // Composables
  const {
    baseModels,
    filteredModels,
    isLoading,
    error,
    providerFilter,
    showEnabledOnly,
    fetchBaseModels,
    addBaseModel,
    enableBaseModel,
    disableBaseModel,
    deleteBaseModel,
  } = useBaseModels();

  // Derive provider options from the loaded catalog so new providers surface
  // automatically as they are seeded upstream.
  const PROVIDER_LABELS: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    groq: 'Groq',
    vertex: 'Vertex',
    bedrock: 'Bedrock',
  };
  const providerOptions = computed(() => {
    const seen = new Set<string>();
    for (const m of baseModels.value) {
      if (m.provider) seen.add(m.provider);
    }
    const opts = [{ value: 'all', label: 'All Providers' }];
    for (const p of Array.from(seen).sort()) {
      opts.push({ value: p, label: PROVIDER_LABELS[p] ?? p });
    }
    return opts;
  });

  const { showSuccess, showError } = useNotification();

  // Local state
  const isAddModalOpen = ref(false);
  const selectedModel = ref<BaseModelResponse | null>(null);

  // Methods
  const handleAdd = (data: { slug: string; name?: string }) => {
    // Find the model to show in the modal
    const model = filteredModels.value.find(m => m.slug === data.slug);
    if (model) {
      selectedModel.value = model;
      isAddModalOpen.value = true;
    }
  };

  const handleAddSubmit = async (data: { slug: string; name?: string }) => {
    try {
      const success = await addBaseModel(data.slug, data.name);
      if (success) {
        showSuccess(
          data.name
            ? `Successfully added ${data.name} to your organization`
            : `Successfully added ${data.slug} to your organization`
        );
        isAddModalOpen.value = false;
        selectedModel.value = null;
        // Refresh the list
        await fetchBaseModels();
      } else {
        showError('Failed to add base model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while adding the base model.');
      console.error('Error adding base model:', err);
    }
  };

  const handleAddCancel = () => {
    isAddModalOpen.value = false;
    selectedModel.value = null;
  };

  const handleEnable = async (slug: string) => {
    try {
      const success = await enableBaseModel(slug);
      if (success) {
        showSuccess(`Successfully enabled ${slug}`);
        // Refresh the list
        await fetchBaseModels();
      } else {
        showError('Failed to enable base model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while enabling the base model.');
      console.error('Error enabling base model:', err);
    }
  };

  const handleDisable = async (slug: string) => {
    try {
      const success = await disableBaseModel(slug);
      if (success) {
        showSuccess(`Successfully disabled ${slug}`);
        // Refresh the list
        await fetchBaseModels();
      } else {
        showError('Failed to disable base model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while disabling the base model.');
      console.error('Error disabling base model:', err);
    }
  };

  const handleRemove = async (slug: string) => {
    try {
      const success = await deleteBaseModel(slug);
      if (success) {
        showSuccess(`Successfully removed ${slug} from your organization`);
        // Refresh the list
        await fetchBaseModels();
      } else {
        showError('Failed to remove base model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while removing the base model.');
      console.error('Error removing base model:', err);
    }
  };

  // Lifecycle
  onMounted(async () => {
    await fetchBaseModels();
  });
</script>
